import React from 'react';
import { emit } from '../Socket/game.Emitters';
import { gameStateManager } from '../../App';

class EmojiChatBox extends React.Component
{
  state = {
    recentReactions: [],
    animatingEmojiIndex: null,
    isMuted: false,
  };

  // Standard emoji set for reactions
  emojis = [ '👍', '👋', '❤️', '😂', '😮', '🤔', '🎉', '✨', '🔥', '😢', '😡', '👏' ];

  constructor( props )
  {
    super( props );
    this.myRef = React.createRef();
    this.reactionTimeoutHandlesByPlayer = new Map();
  }

  componentDidMount()
  {
    gameStateManager.addOnEmojiReactionCallback( this.onEmojiReactionCallback );
  }

  componentWillUnmount()
  {
    gameStateManager.removeOnEmojiReactionCallback( this.onEmojiReactionCallback );

    this.reactionTimeoutHandlesByPlayer.forEach( timeoutHandle => clearTimeout( timeoutHandle ) );
    this.reactionTimeoutHandlesByPlayer.clear();
  }

  getReactionDurationMs = () =>
  {
    const fallbackDuration = 5000;
    const parsedDuration = Number.parseInt( process.env.REACT_APP_EMOJI_REACTION_DURATION, 10 );

    if( Number.isNaN( parsedDuration ) || parsedDuration <= 0 )
    {
      return fallbackDuration;
    }

    return parsedDuration;
  };

  getMaxChainedReactionsPerPlayer = () =>
  {
    const fallbackMax = 5;
    const parsedMax = Number.parseInt( process.env.REACT_APP_MAX_CHAINED_EMOJI_REACTIONS, 10 );

    if( Number.isNaN( parsedMax ) || parsedMax <= 0 )
    {
      return fallbackMax;
    }

    return parsedMax;
  };

  resetReactionTimeoutForPlayer = ( playerName ) =>
  {
    const existingTimeoutHandle = this.reactionTimeoutHandlesByPlayer.get( playerName );

    if( existingTimeoutHandle )
    {
      clearTimeout( existingTimeoutHandle );
    }

    const timeoutHandle = setTimeout( () =>
    {
      this.setState( ( state ) =>
      {
        const updatedReactions = state.recentReactions.filter( reaction => reaction.playerName !== playerName );
        return { recentReactions: updatedReactions };
      } );

      this.reactionTimeoutHandlesByPlayer.delete( playerName );
    }, this.getReactionDurationMs() );

    this.reactionTimeoutHandlesByPlayer.set( playerName, timeoutHandle );
  };

  onEmojiReactionCallback = ( reactionData ) =>
  {
    // reactionData: {playerName: 'PlayerName', emoji: '👍', playerIconID: 'default', teamColourID: 'red'}
    if( !reactionData || !reactionData.playerName || !reactionData.emoji )
    {
      return;
    }

    if( this.state.isMuted && !this.isCurrentPlayerReaction( reactionData ) )
    {
      // While muted, ignore other players' reactions.
      return;
    }

    let didUpdateReactionRow = false;

    this.setState( ( state ) =>
    {
      const existingReactionIndex = state.recentReactions.findIndex( reaction => reaction.playerName === reactionData.playerName );
      let newReactions = [ ...state.recentReactions ];

      if( existingReactionIndex >= 0 )
      {
        const existingReaction = state.recentReactions[ existingReactionIndex ];
        const existingEmojis = existingReaction.emojis || ( existingReaction.emoji ? [ existingReaction.emoji ] : [] );

        if( existingEmojis.length >= this.getMaxChainedReactionsPerPlayer() )
        {
          // This player's row is full. They must wait for it to clear.
          return null;
        }

        const updatedReaction = {
          ...existingReaction,
          emojis: [ ...existingEmojis, reactionData.emoji ],
          messageIconPath: reactionData.messageIconPath || existingReaction.messageIconPath,
        };

        newReactions.splice( existingReactionIndex, 1 );
        newReactions = [ updatedReaction, ...newReactions ];
      }
      else
      {
        const newReaction = {
          playerName: reactionData.playerName,
          messageIconPath: reactionData.messageIconPath,
          emojis: [ reactionData.emoji ],
        };

        newReactions = [ newReaction, ...newReactions ];
      }

      // Keep only the last 8 reactions
      if( newReactions.length > 8 )
      {
        const removedReaction = newReactions.pop();

        if( removedReaction && removedReaction.playerName )
        {
          const removedReactionTimeoutHandle = this.reactionTimeoutHandlesByPlayer.get( removedReaction.playerName );

          if( removedReactionTimeoutHandle )
          {
            clearTimeout( removedReactionTimeoutHandle );
            this.reactionTimeoutHandlesByPlayer.delete( removedReaction.playerName );
          }
        }
      }

      didUpdateReactionRow = true;
      return { recentReactions: newReactions };
    }, () =>
    {
      if( didUpdateReactionRow )
      {
        this.resetReactionTimeoutForPlayer( reactionData.playerName );
      }
    } );
  };

  handleEmojiClick = ( emoji, index ) =>
  {
    if( this.state.isMuted )
    {
      return;
    }

    // Trigger animation
    this.setState( { animatingEmojiIndex: index } );
    setTimeout( () =>
    {
      this.setState( { animatingEmojiIndex: null } );
    }, 300 );

    // Emit the emoji reaction to other players
    emit.submitEmojiReaction( emoji );
  };

  getCurrentPlayerName = () =>
  {
    const clientIndex = gameStateManager.getSocketClientIndex();
    const playersJoined = gameStateManager.gameStateData?.playerData?.playersJoined;

    if( typeof clientIndex === 'undefined' || !playersJoined || clientIndex < 0 || clientIndex >= playersJoined.length )
    {
      return undefined;
    }

    return playersJoined[ clientIndex ]?.username;
  };

  isCurrentPlayerReaction = ( reactionData ) =>
  {
    const currentPlayerName = this.getCurrentPlayerName();
    return !!currentPlayerName && reactionData.playerName === currentPlayerName;
  };

  onToggleMute = () =>
  {
    this.setState( state => ( { isMuted: !state.isMuted } ) );
  };

  render()
  {
    const { width, height, top, right } = this.props;
    const { recentReactions, animatingEmojiIndex, isMuted } = this.state;

    const visibleReactions = isMuted ? recentReactions.filter( reaction => this.isCurrentPlayerReaction( reaction ) ) : recentReactions;
    const emptyStateText = isMuted ? 'Chat is muted' : 'No reactions yet';

    return (
      <div
        className="emoji-chat-box"
        style={ {
          width: width + 'px',
          height: height + 'px',
          top: top + 'px',
          right: right + 'px',
        } }
      >
        <div className="emoji-chat-header-row">
          <div className="emoji-chat-header">Reactions</div>
          <button
            type="button"
            className={ `emoji-chat-mute-toggle ${isMuted ? 'muted' : ''}` }
            onClick={ this.onToggleMute }
            title={ isMuted ? 'Unmute emoji chat' : 'Mute emoji chat' }
            aria-label={ isMuted ? 'Unmute emoji chat' : 'Mute emoji chat' }
          >
            { isMuted ? 'Unmute' : 'Mute' }
          </button>
        </div>

        <div className="emoji-chat-content">
          <div className={ `emoji-grid ${isMuted ? 'muted' : ''}` }>
            { this.emojis.map( ( emoji, index ) => (
              <button
                key={ index }
                className={ `emoji-button ${animatingEmojiIndex === index ? 'emoji-animating' : ''}` }
                onClick={ () => this.handleEmojiClick( emoji, index ) }
                title={ `Send ${emoji}` }
                disabled={ isMuted }
              >
                { emoji }
              </button>
            ) ) }
          </div>

          <div className="recent-reactions-container">
            {/* <div className="recent-reactions-title">Recent</div> */ }
            <div className="recent-reactions-feed">
              { visibleReactions.length === 0 ? (
                <div className="no-reactions">{ emptyStateText }</div>
              ) : (
                visibleReactions.map( ( reaction, index ) => (
                  <div key={ index } className="reaction-entry">
                    { reaction.messageIconPath && (
                      <img
                        src={ reaction.messageIconPath }
                        alt="Player icon"
                        className="reaction-player-icon"
                      />
                    ) }
                    <div className="reaction-text-playername">
                      { reaction.playerName }
                    </div>

                    <span className="reaction-emoji">
                      { ( reaction.emojis || ( reaction.emoji ? [ reaction.emoji ] : [] ) ).join( ' ' ) }
                    </span>
                  </div>
                ) )
              ) }
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default EmojiChatBox;
